import _ from 'lodash';
import { useState, useCallback } from 'react';
import useSirenEntity from './use-siren-entity.ts';
import { submitAction, parseAction } from './siren-actions.js';

export function useSirenActionFields({
  entity,
  actionName,
  defaultFieldOverrides,
}) {
  const sirenAction = entity?.getActionByName(actionName);
  const [modifiedFields, _setModifiedFields] = useState({});
  const editActionField = (name, value) => {
    _setModifiedFields({ ...modifiedFields, [name]: value });
  };

  let fields;
  let defaultFields;
  let hasModifiedFields = false;
  if (sirenAction) {
    fields = {};
    defaultFields = {};

    const overriddenDefaultFields = Object.keys(defaultFieldOverrides || {});
    const sirenActionFields = sirenAction.fields || [];
    for (const field of sirenActionFields) {
      defaultFields[field.name] = overriddenDefaultFields.includes(field.name)
        ? defaultFieldOverrides[field.name]
        : getSirenFieldValue(field);

      fields[field.name] =
        modifiedFields[field.name] ?? defaultFields[field.name] ?? '';
    }

    hasModifiedFields = !_.isEqual(fields, defaultFields);
  }

  const requiredFields = sirenAction?.fields?.filter((field) => field.class?.includes('required')) || [];

  return {
    defaultFields,
    requiredFieldNames: requiredFields.map((field) => field.name),
    clearModifiedFields: () => _setModifiedFields({}),
    hasModifiedFields,
    editActionField: sirenAction && editActionField,
    sirenAction,
    fields,
  };
}

export function useSirenActionSubmitter({ sirenAction, fields = {}, opts = {} }) {
  const [submitting, setSubmitting] = useState(false);
  const [responseObjects, setResponseObjects] = useState({});

  const submit = useCallback(async () => {
    if (submitting || !sirenAction) {
      return;
    }

    setSubmitting(true);

    let entity;
    let response;
    let error;
    try {
      ({ entity, response } = await submitAction(sirenAction, fields, opts));
    } catch (err) {
      error = err;
    }

    setSubmitting(false);
    setResponseObjects({ entity, response, error });
    return { entity, response, error };
  }, [submitting, sirenAction, fields, opts]);

  return {
    submit,
    clearError: () => setResponseObjects(_.omit(responseObjects, ['error'])),
    submitting,
    ...responseObjects,
  };
}

/**
 * Uses the entity (or fetches it with using href) to find the entity denoted by actionName
 * useSirenGetActionEntity({ href, actionName: 'limit', fields: { limit: 10 } })
 * useSirenGetActionEntity({ entity, actionName: 'limit, fields: { limit: 10 } })
 */
export function useSirenGetActionEntity({ href, entity: suppliedEntity, actionName, fields = {}, options = {} }) {
  const { entity: fetchedEntity, loading: loadingBase } = useSirenEntity({ href });
  const normalizedEntity = suppliedEntity || fetchedEntity;
  const sirenAction = normalizedEntity?.getActionByName(actionName);
  const { url } = sirenAction ? parseAction(sirenAction, fields, options) : {};
  const { entity, loading } = useSirenEntity({ href: url?.href });
  return { entity, loading: loading || loadingBase };
}

export function getSirenFieldValue (field) {
  switch (field.type) {
  case 'text':
  case 'email':
  case 'hidden':
  case 'password':
  case 'date':
  case 'datetime':
  case 'datetime-local':
  case 'number':
  case 'tel':
  case 'time':
    return field.value;
  case 'radio':
    return field.value.find((val) => val.checked)?.value;
  case 'checkbox':
    return field.value.filter((val) => val.checked).map((val) => val.value);
  case 'color':
  case 'range':
  case 'week':
  case 'month':
  case 'url':
  case 'search':
    throw new Error(
      `Siren field type "${field.type}" handling not implemented`,
    );
  default:
    throw new Error(
      `Non Siren spec field type returned by a siren entity "${field.type}"`,
    );
  }
}
